import Vue from 'vue'
import i18next from 'i18next'
import axios from 'axios'
import { Auth, Hub, I18n as AmplifyI18n } from 'aws-amplify'
import es from 'vuetify/lib/locale/es'
import en from 'vuetify/lib/locale/en'

const namespaces = ['common', 'public', 'records', 'account', 'admin', 'legal']
const normalize = value => (/^es(?:[-_].*)?$/i.test(String(value || '')) ? 'es-ES' : 'en')
const state = Vue.observable({ locale: 'en', revision: 0, ready: false })
let vuetify
let accountLocaleApplied = false
let changeNumber = 0
const loaded = new Map()
const textKey = value => String(value == null ? '' : value).replace(/\s+/g, ' ').trim()
const initialLocale = () => {
  try { const saved = localStorage.getItem('sb-locale'); if (saved) return normalize(saved) } catch (_) { /* Optional storage. */ }

  return normalize((navigator.languages && navigator.languages[0]) || navigator.language)
}

export function t(message, variables = {}) {
  // Reading the observable makes translations reactive in Vue 2 templates/getters.
  const { locale } = state
  const { revision } = state
  void revision
  const key = textKey(message)
  if (!key) return message == null ? '' : String(message)
  const original = String(message)
  const prefix = (original.match(/^\s*/) || [''])[0]
  const suffix = (original.match(/\s*$/) || [''])[0]

  return prefix + i18next.t(key, {
    lng: locale, ns: namespaces, defaultValue: key, ...variables,
  }) + suffix
}
export function currentLocale() { return state.locale }
export function authMessage(message) {
  // Legacy Amplify children may already hold a translated label at mount time.
  // Resolve that library UI text back to its source key before changing locale.
  const key = textKey(message)
  for (const ns of namespaces) {
    const catalog = i18next.getResourceBundle('es-ES', ns) || {}
    const original = Object.keys(catalog).find(source => textKey(catalog[source]) === key)
    if (original) return t(String(message).replace(key, original))
  }
  return t(message)
}
export function intlLocale() { return state.locale === 'es-ES' ? 'es-ES' : 'en-US' }
export function systemMessage(message) {
  const text = String(message || '')
  const split = text.indexOf(' Reason: ')
  if (split !== -1) return `${t(text.slice(0, split))} ${t('Reason:')} ${text.slice(split + 9)}`
  const suffix = ' No detailed reason was returned.'
  if (text.endsWith(suffix)) return `${t(text.slice(0, -suffix.length))} ${t(suffix.trim())}`
  return t(text)
}
const technicalKeys = new Set(['id', 'type', 'to', 'path', 'url', 'href', 'src', 'icon', 'image', 'anchor', 'related', 'category', 'device', 'updatedDate', 'status', 'currency'])

// This is used only with application-owned imported copy, never with API user content.
export function localizeCopy(value, key = '') {
  if (technicalKeys.has(key)) return value
  if (typeof value === 'string') return t(value)
  if (Array.isArray(value)) return value.map(item => localizeCopy(item, key))
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, localizeCopy(v, k)]))

  return value
}
export function localizeMarkup(html) {
  const root = document.createElement('template')
  root.innerHTML = html || ''
  const walker = document.createTreeWalker(root.content, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) { if (!node.parentElement || !['SCRIPT', 'STYLE', 'CODE', 'PRE'].includes(node.parentElement.tagName)) node.textContent = t(node.textContent); node = walker.nextNode() }

  return root.innerHTML
}
export async function loadLocale(area = 'common', language = state.locale) {
  const ns = namespaces.includes(area) ? area : 'common'
  const key = `${language}:${ns}`
  if (!loaded.has(key)) {
    loaded.set(key, import(`./locales/${language}/${ns}.json`).then(module => {
      i18next.addResourceBundle(language, ns, module.default || module, true, true)
      if (language === 'es-ES') AmplifyI18n.putVocabulariesForLanguage('es', module.default || module)
      state.revision += 1
    }).catch(error => { loaded.delete(key); throw error }))
  }

  return loaded.get(key)
}
export async function loadRouteLanguage(route) {
  const path = route.path || '/'
  const area = path.startsWith('/admin') ? 'admin' : path.startsWith('/pages') ? 'legal'
    : /^\/(billing|referrals|account|auth)/.test(path) ? 'account'
      : /^\/(my-|import|chat)/.test(path) ? 'records' : 'public'
  await Promise.all(['common', 'records', 'account', area].map(ns => loadLocale(ns)))
}
export async function setLocale(value, { persist = true, saveAccount = true } = {}) {
  const locale = normalize(value)
  const number = ++changeNumber
  const active = Array.from(loaded.keys()).filter(key => key.startsWith(`${state.locale}:`)).map(key => key.split(':')[1])
  await Promise.all([...new Set(['common', 'records', ...active])].map(ns => loadLocale(ns, locale)))
  if (number !== changeNumber) return
  state.locale = locale
  await i18next.changeLanguage(locale)
  if (vuetify) vuetify.framework.lang.current = locale === 'es-ES' ? 'es' : 'en'
  document.documentElement.lang = locale
  AmplifyI18n.setLanguage(locale === 'es-ES' ? 'es' : 'en')
  Auth.configure({ clientMetadata: { locale } })
  state.revision += 1
  if (persist) { try { localStorage.setItem('sb-locale', locale) } catch (_) { /* Optional storage. */ } }
  window.dispatchEvent(new CustomEvent('locale:changed', { detail: locale }))
  if (saveAccount) {
    try {
      const session = await Auth.currentSession()
      await axios.post(`${process.env.VUE_APP_ROOT_API}/updateUserData`, { preferred_locale: locale }, { headers: { Authorization: session.getAccessToken().getJwtToken() } })
      const user = await Auth.currentAuthenticatedUser()
      await Promise.allSettled([
        Auth.updateUserAttributes(user, { locale }),
        axios.post(`${process.env.VUE_APP_ROOT_API}/billing/locale`, { locale }, { headers: { Authorization: session.getAccessToken().getJwtToken() } }),
      ])
    } catch (_) { /* Keep the local choice if offline or signed out. */ }
  }
}
export async function initializeLanguage(ui) {
  vuetify = ui
  ui.framework.lang.locales.es = { ...es, carousel: { prev: 'Imagen anterior', next: 'Imagen siguiente', ariaLabel: { delimiter: 'Diapositiva {0} de {1}' } }, rating: { ariaLabel: { icon: 'Valoración {0} de {1}' } } }
  ui.framework.lang.locales.en = en
  await i18next.init({
    lng: 'en', fallbackLng: 'en', resources: {}, ns: namespaces, defaultNS: 'common', keySeparator: false, nsSeparator: false, interpolation: { escapeValue: false }, returnEmptyString: false,
  })
  try { await setLocale(initialLocale(), { persist: false, saveAccount: false }) } catch (_) {
    // Natural-language keys keep the product usable when a catalog is unavailable.
    state.locale = 'en'
    await i18next.changeLanguage('en')
    ui.framework.lang.current = 'en'
    document.documentElement.lang = 'en'
    AmplifyI18n.setLanguage('en')
    Auth.configure({ clientMetadata: { locale: 'en' } })
  }
  state.ready = true
}
const categoryKeys = new Set(['My Family', 'My Friends', 'My Pets', 'My Things', 'My Places', 'My Self', 'My Soul', 'My Beliefs', 'My Faith', 'My Relationships', 'My Work', 'My Bussiness', 'My Accomplishment', 'My Aspirations', 'My Projects', 'My Resume', 'My Worlds', 'My Media'])
Vue.prototype.$categoryName = category => (categoryKeys.has(category.name) ? t(category.name) : category.name)
Vue.prototype.$t = t
Vue.prototype.$i18n = state
Vue.prototype.$locale = function () { return intlLocale() }
Vue.mixin({ computed: { languageRevision() { return state.revision } } })
axios.interceptors.request.use(config => {
  const url = config.url || ''
  if (/\/(getPublicItems|getPublicUser|getPublicCategories|getPage|getNotifications)(?:\?|$)/.test(url)) config.params = { ...config.params, locale: state.locale }

  return config
})
axios.interceptors.response.use(response => {
  if (/\/getUser(?:\?|$)/.test(response.config.url || '')) {
    const rows = response.data && response.data.content
    const preferred = rows && rows[0] && rows[0].preferred_locale
    if (rows && rows.length && !accountLocaleApplied) { accountLocaleApplied = true; void setLocale(preferred || state.locale, { persist: true, saveAccount: !preferred }) }
  }

  return response
}, error => {
  if (error.response && error.response.data) {
    const body = error.response.data
    if (typeof body.message === 'string') body.message = t(body.message)
    if (typeof body.error === 'string' && /\s/.test(body.error)) body.error = t(body.error)
  }

  return Promise.reject(error)
})
window.addEventListener('storage', event => { if (event.key === 'sb-locale' && event.newValue) void setLocale(event.newValue, { persist: false, saveAccount: false }) })
Hub.listen('auth', ({ payload }) => { if (payload.event === 'signOut' || payload.event === 'signIn') accountLocaleApplied = false })
export default state
